import { OrderTradeProgress } from '@meitu/subscribe';
import { initBasicAnalysis } from './utils';

type Env = 'pre' | 'beta' | 'release';
// 埋点文档 https://dayu.starii-rover.com/requirement/requirement?sso_code=7218504577643970560_1721025604

const DEFAULT_CONFIG = {
    debug_mode: true,
    traffic_type: 'internal',
};
/**
 * @description GAEventTracker 类 谷歌自定义事件埋点
 */
class GAEventTracker {
    private static instance: GAEventTracker;
    //   埋点入口参数
    private entrance: string | null;
    //  是否暂停ga 收集
    private isStopGa: boolean;

    // 环境
    private env: Env;

    private constructor() {
        this.entrance = null;
        this.isStopGa = false; // 默认收集 ga 自定义事件
        this.env = 'pre'; // 默认为pre环境
    }

    /**
     * @description 获取当前埋点入口
     * @returns
     */
    getEntrance(): string | null {
        return this.entrance;
    }

    /**
     * @description 设置当前埋点入口
     * @param newEntrance
     * @example
     *     比如用户在导航登录按钮点击之后需要设置当前埋点入口为 TopNav
     *     gaEventTracker.setEntrance('TopNav')
     */
    setEntrance(newEntrance: string): void {
        this.entrance = newEntrance;
    }

    /**
     * @description 获取是否暂停ga 收集 状态
     * @returns
     */
    getIsStopGa(): boolean {
        return this.isStopGa;
    }

    /**
     * @description 设置是否暂停ga 收集
     * @param {boolean} bool     是否暂停
     * @example
     *     setStopGa(true)   // 停止收集
     *     setStopGa(false)  // 开始收集
     */
    setStopGa(bool: boolean): void {
        this.isStopGa = bool;
    }

    /**
     * @description 设置配置
     * @param {"pre"|"beta"|"release"}  pre                          环境
     * @param {boolean} [isStopGa = false] isStopGa                  是否暂停ga 收集 默认为false
     * @example
     *     setConfig({env:'pre'}) // 设置环境为pre
     *     setConfig({env:'pre',isStopGa: true}) // 设置环境为pre,暂停ga 自定义事件收集
     */
    setConfig(config: {
        env: Env;
        isStopGa?: boolean;
        hasViewMoreThan1Min?: boolean;
        hasViewMoreThan2Pages?: boolean;
    }): void {
        const { env, isStopGa = false, hasViewMoreThan1Min = true, hasViewMoreThan2Pages = true } = config;
        this.env = env;
        this.isStopGa = isStopGa;
        // 默认添加 网页浏览超过2页面 & 网页浏览超过1分钟 统计
        if (typeof window !== 'undefined') {
            initBasicAnalysis({
                hasViewMoreThan2Pages,
                hasViewMoreThan1Min,
                callback: (eventName: string, eventParams: any) => {
                    this.sendGaEvent(eventName, eventParams);
                },
            });
        }
    }

    /**
     * @description 获取当前环境
     * @returns
     */
    getEnv(): Env {
        return this.env;
    }

    /**
     * @description 获取GAEventTracker实例
     * @returns
     */
    static getInstance(): GAEventTracker {
        if (!GAEventTracker.instance) {
            GAEventTracker.instance = new GAEventTracker();
        }
        return GAEventTracker.instance;
    }

    /**
     * @description 发送GA事件
     * @param {string} eventName
     * @param {any} eventParams
     * @example
     *     目前之封装了注册|登录、购买成功的、保存成功的事件（都是基于 sendGaEvent 事件来的），如果其他事件需要发送，可以直接调用sendGaEvent方法
     */
    sendGaEvent(eventName: string, eventParams: any) {
        if (this.isStopGa) {
            // 暂停事件收集，直接退出
            return;
        }
        // 发送GA事件
        // debug_mode 模式打开可在谷歌分析后台debugview 里查看实时事件
        // traffic_type = internal 区分内部测试数据，beta|pre 环境都是内部测试数据，release 环境是正式数据
        if (window.dataLayer && window.gtag) {
            let params = eventParams;
            if (this.env !== 'release') {
                params = { ...params, ...DEFAULT_CONFIG };
            }
            window.gtag?.('event', eventName, params);
        }
    }

    /**
     * @description 发送登录/注册结果GA事件
     * @param {boolean} isRegister                          是否注册  MeituAccount里的 onLoginSuccess 第二个参数可以区分是否注册
     * @param {string|undefined} [platform='email']         平台（登录方式） MeituAccount里的 onLoginSuccess 第一个参数的platform属性 可以区分平台，
     * @param {string} pageName                             页面名称
     * @param {string|undefined} [entrance]                 入口名称
     * @example
     *     MeituAccount({
     *            ......
     *            onLoginSuccess: (result, isRegister) => {
     *                    //result.platform 是存在undefined 的情况的
     *                    const pageName = 'home'
     *                    const entrance = 'xxxxx' // 入口名称
     *                    gaEventTracker.sendLoginGaTrack(isRegister, result.platform, pageName, entrance)
     *            }
     *            ....
     *    })
     * @see MeituAccount 登录注册接口文档 https://f2er.meitu.com/account/api/account-alipay-mini-program/function/onLoginSuccess
     */
    sendLoginGaTrack(isRegister: boolean, platform: string = 'email', pageName: string, entrance?: string) {
        // ads转化代码
        if (isRegister) {
            this.sendGaEvent('conversion', {
                send_to: 'AW-871914325/fpXBCIPN5ssZENW24Z8D',
            });
        }
        this.sendGaEvent(isRegister ? 'register_result' : 'login_result', {
            page_name: pageName,
            method: platform,
            entrance: entrance || this.entrance || 'topnav',
        });
    }

    /**
     * @description 发送购买结果GA事件
     * @param {OrderTradeProgress[]} tradeProgress        订单交易数据
     * @param {boolean} isVip                             是否是会员
     * @param {string} funcSource                         功能来源
     * @param {string|undefined} [entrance]               入口名称
     * @example
     *    openSubscribePopup({
     *            ......
     *            onTradeFinish(tradeProgress) {
     *                const isVip = true // 假设是会员
     *                const funcSource = 'home' // 假设是首页
     *                const entrance = 'xxxxx' // 入口名称
     *                gaEventTracker.sendPurchaseGaTrack(tradeProgress, isVip, funcSource,entrance)
     *            }
     *            ......
     *    })
     * @see tradeProgress 交易接口文档 https://f2er.meitu.com/subscribe/api/subscribe-core/interface/OrderTradeProgressProduct
     */
    sendPurchaseGaTrack(tradeProgress: OrderTradeProgress[], isVip: boolean, funcSource: string, entrance?: string) {
        tradeProgress.forEach((v, i) => {
            const {
                product: { productId, moneyUnit, price, isProbation },
                payChannel,
                transactionId,
            } = v;

            const gaData = {
                value: price,
                currency: moneyUnit,
                transaction_id: transactionId,
                user_type: isVip ? 1 : 0,
                item_id: productId,
                payment_type: payChannel,
                is_try: isProbation ? 0 : 1,
                func_source: funcSource,
                entrance: entrance || this.entrance || 'topnav',
            };
            // ads转化代码
            this.sendGaEvent('conversion', {
                send_to: 'AW-871914325/CMpSCIzN5ssZENW24Z8D',
            });
            // ga
            this.sendGaEvent('purchase', gaData);
        });
    }

    /**
     * @description     发送保存成功GA事件
     * @param {string} from                               来源
     * @param {string} func                               功能名称
     * @example
     *      const from = 'home';// 假设是home, 可以调用 getFrom() 方法获取来源，如果有的话
     *      const func = 'create_now';// 假设是create_now
     *      sendSaveSuccessGaTrack(from, func)
     */
    sendSaveSuccessGaTrack(from: string, func: string = 'create_now') {
        // 获取链接from参数
        this.sendGaEvent('save_success', {
            source: from || 'other',
            func,
        });
    }

    sendUploadClickGaTrack() {
        // 获取链接from参数
        this.sendGaEvent('upload_click', {});
    }
}

const gaEventTracker = GAEventTracker.getInstance();
export default gaEventTracker;
